import { useRef, useState } from 'react';
import clsx from 'clsx';
import { Card } from '../../../layout/Card.jsx';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { Avatar } from '../../../layout/Avatar.jsx';
import { formatIQD, formatDate } from '../../../shared/format.js';
import { useAuth } from '../../../shared/useAuth.js';
import { apiError } from '../../../shared/api.js';
import { toast } from '../../../shared/useToast.js';
import {
  useSubscriptionSearch,
  fetchSubscriptionReceipt,
  useSellSubscription,
  useRenewSubscription,
  useCancelSubscription,
  STATUS_LABELS,
  STATUS_VARIANTS
} from '../hooks/useSubscriptions.js';
import { SubscribeModal } from './SubscribeModal.jsx';
import { ConfirmModal } from './ConfirmModal.jsx';
import { SubscriptionSuccessModal } from './SubscriptionSuccessModal.jsx';

const FILTERS = [
  { key: '', label: 'الكل' },
  { key: 'active', label: 'فعال' },
  { key: 'expired', label: 'منتهي' },
  { key: 'cancelled', label: 'ملغى' }
];

export function SubscribersList() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const canSell = hasPermission('reception.create_session')
    || hasPermission('reception.issue_card')
    || hasPermission('pricing.edit');
  const canCancel = hasPermission('pricing.edit') || hasPermission('checkout.override');

  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  // النص جاء من قارئ البطاقات؟ يجعل تشخيص «البطاقة غير مسجلة» قاطعاً بلا
  // تبديل أوضاع في الواجهة — الموظف يرى حقلاً واحداً فقط.
  const [scanned, setScanned] = useState(false);
  const searchRef = useRef(null);
  const [sellOpen, setSellOpen] = useState(false);
  const [renewing, setRenewing] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [reason, setReason] = useState('');
  const [success, setSuccess] = useState(null);

  // كشف «جاء من القارئ» بلا وضع خاص: قارئ RFID يكتب الرقم كدفعة واحدة وينهيه
  // بـEnter. فالدفعة الكبيرة (≥4 خانات في حدث واحد) أو ضغط Enter تعني بطاقة،
  // والكتابة حرفاً حرفاً تعني اسماً. لذلك لا نستعمل useRfidScan هنا: مهلته
  // الزمنية (200مس سكون) لا تفرّق بين قارئ وكاتب بطيء، فكانت تُحوِّل كل اسم
  // مكتوب إلى «بطاقة» فتظهر رسالة «البطاقة غير مسجلة» على اسم عادي.
  const onSearchChange = (e) => {
    const next = e.target.value;
    const burst = next.length - String(q || '').length >= 4;
    setQ(next);
    setScanned(burst);
  };

  const { data, isLoading, error } = useSubscriptionSearch({ q, status, scanned });
  const rows = (data && data.results) || [];
  const cardInfo = (data && data.card) || null;
  const notice = (data && data.notice) || null;

  const sell = useSellSubscription();
  const renew = useRenewSubscription();
  const cancel = useCancelSubscription();

  // إعادة الطباعة تقرأ الإيصال من سجله المالي الأصلي ولا تكتب شيئاً.
  const reprint = async (row) => {
    try {
      const receipt = await fetchSubscriptionReceipt(row._id || row.id);
      setSuccess({ receipt, created: null, title: 'إيصال الاشتراك الشهري' });
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1 p-1 bg-white border border-line rounded-[10px]">
            {FILTERS.map((f) => (
              <button
                key={f.key || 'all'}
                type="button"
                onClick={() => setStatus(f.key)}
                className={clsx(
                  'px-3 py-1.5 rounded-[8px] text-[12.5px] font-semibold transition-colors',
                  status === f.key ? 'bg-orange text-white' : 'text-ink-2 hover:bg-paper-2'
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <input
              ref={searchRef}
              className="input pr-9"
              style={{ width: 280 }}
              placeholder="بحث باسم الطفل أو مرّر البطاقة"
              value={q}
              onChange={onSearchChange}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setScanned(true); } }}
            />
            <Icon
              name="search"
              size={15}
              className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-3 pointer-events-none"
            />
          </div>
          {q ? (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ padding: '6px 8px', fontSize: 12 }}
              onClick={() => { setQ(''); setScanned(false); }}
            >
              <Icon name="x" size={13} /> مسح
            </button>
          ) : null}
        </div>
        {canSell ? (
          <button type="button" className="btn btn-primary" onClick={() => setSellOpen(true)}>
            <Icon name="plus" size={14} /> اشتراك شهري جديد
          </button>
        ) : null}
      </div>

      {cardInfo ? (
        <div
          className="rounded-[10px] px-3 py-2 text-[12.5px] flex items-center gap-2 flex-wrap"
          style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', color: '#0E7490' }}
        >
          <Icon name="card" size={14} />
          <span>
            البطاقة <b dir="ltr" style={{ fontFamily: 'monospace' }}>{cardInfo.uid}</b> مسجّلة
            {cardInfo.childName ? ` — الطفل ${cardInfo.childName}` : ''} • الرصيد {formatIQD(cardInfo.balance || 0)}
          </span>
        </div>
      ) : null}

      {notice ? (
        <div
          className="rounded-[10px] px-3 py-2 text-[12.5px]"
          style={{ background: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E' }}
        >
          {notice}
        </div>
      ) : null}

      {error ? (
        <Card><div className="text-red text-center py-4">تعذّر تحميل الاشتراكات</div></Card>
      ) : isLoading ? (
        <Card><div className="text-ink-3 text-center py-8">جاري التحميل...</div></Card>
      ) : rows.length === 0 ? (
        <Card>
          <div className="text-ink-3 text-center py-10 flex flex-col items-center gap-3">
            <Icon name="card" size={42} color="#D6CCB3" />
            <div className="text-[14px] font-semibold text-ink-2">
              {q ? 'لا نتائج مطابقة' : 'لا اشتراكات بعد'}
            </div>
          </div>
        </Card>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="bg-paper-2 text-ink-2">
                <th className="text-right font-semibold px-3 py-2">الطفل</th>
                <th className="text-right font-semibold px-3 py-2">البطاقة</th>
                <th className="text-right font-semibold px-3 py-2">الباقة</th>
                <th className="text-right font-semibold px-3 py-2">البداية</th>
                <th className="text-right font-semibold px-3 py-2">الانتهاء</th>
                <th className="text-right font-semibold px-3 py-2">المتبقي</th>
                <th className="text-right font-semibold px-3 py-2">المدفوع</th>
                <th className="text-right font-semibold px-3 py-2">الحالة</th>
                <th className="text-right font-semibold px-3 py-2"> </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((s) => {
                const childName = s.childId?.name || 'طفل محذوف';
                const expiringSoon = s.status === 'active' && Number(s.daysRemaining) <= 7;
                const uid = s.cardUid || s.childId?.cardUid || '';
                return (
                  <tr key={s._id || s.id}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Avatar name={childName} id={String(s.childId?._id || '')} src={s.childId?.photoUrl} size="sm" />
                        <div>
                          <div className="font-semibold">{childName}</div>
                          <div className="text-[11px] text-ink-3">{s.guardianId?.phone || ''}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2" dir="ltr" style={{ fontFamily: 'monospace', textAlign: 'right' }}>
                      {uid || '—'}
                    </td>
                    <td className="px-3 py-2">
                      {s.planName || '—'}
                      {s.renewalsCount > 0 ? (
                        <Pill variant="info" className="mr-1.5">{s.renewalsCount} تجديد</Pill>
                      ) : null}
                    </td>
                    <td className="px-3 py-2" dir="ltr" style={{ textAlign: 'right' }}>{formatDate(s.startDate)}</td>
                    <td className="px-3 py-2" dir="ltr" style={{ textAlign: 'right' }}>{formatDate(s.endDate)}</td>
                    <td className="px-3 py-2">
                      {s.status === 'active' ? (
                        <span className={expiringSoon ? 'text-red font-bold' : ''}>{s.daysRemaining} يوم</span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2">{formatIQD(s.totalPaid || 0)}</td>
                    <td className="px-3 py-2">
                      <Pill variant={STATUS_VARIANTS[s.status] || 'muted'}>
                        {STATUS_LABELS[s.status] || s.status}
                      </Pill>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 justify-end">
                        {(s.periods || []).length > 0 ? (
                          <button
                            type="button"
                            className="btn"
                            style={{ padding: '4px 10px', fontSize: 11.5 }}
                            onClick={() => reprint(s)}
                            title="إعادة طباعة إيصال آخر عملية — لا تُنشئ اشتراكاً ولا دفعة"
                          >
                            <Icon name="print" size={12} /> إيصال
                          </button>
                        ) : null}
                        {canSell && s.status !== 'cancelled' ? (
                          <button
                            type="button"
                            className="btn"
                            style={{ padding: '4px 10px', fontSize: 11.5 }}
                            onClick={() => setRenewing(s)}
                          >
                            تجديد
                          </button>
                        ) : null}
                        {canCancel && s.status !== 'cancelled' ? (
                          <button
                            type="button"
                            className="btn btn-danger"
                            style={{ padding: '4px 10px', fontSize: 11.5 }}
                            onClick={() => { setReason(''); setCancelling(s); }}
                          >
                            إلغاء
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <SubscribeModal
        open={sellOpen}
        submitting={sell.isPending}
        onClose={() => setSellOpen(false)}
        onSubmit={async (body) => {
          try {
            const saved = await sell.mutateAsync(body, { onSuccess: () => setSellOpen(false) });
            // زر الطباعة لا يظهر إلا هنا — بعد نجاح الحفظ وتسجيل المبلغ فعلاً.
            if (saved && saved.receipt) {
              setSuccess({ receipt: saved.receipt, created: saved.created || null });
            }
          } catch (_) {
            // الرسالة ظهرت من useApiMutation — النافذة تبقى مفتوحة ليصحّح الموظف
          }
        }}
      />

      <SubscriptionSuccessModal
        open={!!success}
        receipt={success?.receipt}
        created={success?.created}
        title={success?.title}
        onClose={() => setSuccess(null)}
      />

      <ConfirmModal
        open={!!renewing}
        title="تجديد الاشتراك"
        message={
          renewing
            ? (renewing.status === 'active'
              ? `سيُمدَّد اشتراك ${renewing.childId?.name || ''} ${renewing.durationDays} يوماً من ${formatDate(renewing.endDate)}، ويُستلم ${formatIQD(renewing.price || 0)} نقداً في درج الشفت. رصيد البطاقة لا يتغيّر.`
              : `سيُعاد تفعيل اشتراك ${renewing.childId?.name || ''} لمدة ${renewing.durationDays} يوماً من اليوم، ويُستلم ${formatIQD(renewing.price || 0)}. رصيد البطاقة لا يتغيّر.`)
            : ''
        }
        confirmLabel="تجديد واستلام المبلغ"
        loading={renew.isPending}
        onClose={() => setRenewing(null)}
        onConfirm={async () => {
          try {
            const saved = await renew.mutateAsync(
              { id: renewing._id || renewing.id, body: { paymentMethod: 'cash' } },
              { onSuccess: () => setRenewing(null) }
            );
            if (saved && saved.receipt) setSuccess({ receipt: saved.receipt, created: null });
          } catch (_) { /* الرسالة ظهرت من useApiMutation */ }
        }}
      />

      <ConfirmModal
        open={!!cancelling}
        title="إلغاء الاشتراك"
        message={`سيتوقف اشتراك ${cancelling?.childId?.name || ''} عن تغطية أي جلسة جديدة فوراً، وتعود الجلسات للتسعير العادي. لا يُرَد أي مبلغ تلقائياً ولا يُمسّ رصيد البطاقة.`}
        confirmLabel="إلغاء الاشتراك"
        variant="danger"
        loading={cancel.isPending}
        onClose={() => setCancelling(null)}
        onConfirm={async () => {
          try {
            await cancel.mutateAsync(
              { id: cancelling._id || cancelling.id, reason },
              { onSuccess: () => setCancelling(null) }
            );
          } catch (_) { /* الرسالة ظهرت من useApiMutation */ }
        }}
      >
        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">السبب (اختياري)</label>
          <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="مثلاً: طلب ولي الأمر" />
        </div>
      </ConfirmModal>
    </div>
  );
}
